import clsx from "clsx";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronRight,
  StarIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  XIcon,
} from "lucide-react";

import {
  JobPipelineCandidateSheetVerificationBadge,
  RichTextDisplay,
} from "@/components";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import {
  useGetCandidacyComments,
  useGetCandidacyInterests,
  useGetCandidacyScoreChanges,
  useGetCandidacyStatusChanges,
  useGetCandidacySummaryChanges,
  useGetUser,
} from "@/hooks/queries";
import { candidacyStatusToLabel, candidacyStatusToRank } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { misc } from "@/lib/misc";
import { storage } from "@/lib/storage";
import {
  CandidacyComment as CandidacyCommentType,
  CandidacyInterest as CandidacyInterestType,
  CandidacyScoreChange as CandidacyScoreChangeType,
  CandidacyStatus,
  CandidacyStatusChange as CandidacyStatusChangeType,
  CandidacySummaryChange as CandidacySummaryChangeType,
  Role,
  User,
} from "@/types";

type Activity =
  | {
      type: "candidacyComment";
      candidacyComment: CandidacyCommentType;
      createdAt: string;
    }
  | {
      type: "candidacyScoreChange";
      candidacyScoreChange: CandidacyScoreChangeType;
      createdAt: string;
    }
  | {
      type: "candidacyStatusChange";
      candidacyStatusChange: CandidacyStatusChangeType;
      createdAt: string;
    }
  | {
      type: "candidacySummaryChange";
      candidacySummaryChange: CandidacySummaryChangeType;
      createdAt: string;
    }
  | {
      type: "candidacyInterest";
      candidacyInterest: CandidacyInterestType;
      createdAt: string;
    };

const Avatar = ({ initials }: { initials: string }) => {
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200">
      <p className="text-[10px] font-medium leading-none text-slate-900">
        {initials}
      </p>
    </div>
  );
};

const CandidacyInterest = ({
  candidacyInterest,
  candidateFirstName,
  candidateLastName,
  currentUserId,
}: {
  candidacyInterest: CandidacyInterestType;
  candidateFirstName: string;
  candidateLastName: string;
  currentUserId: number;
}) => {
  const { data: user, status } = useGetUser({
    userId: candidacyInterest.userId,
  });

  const candidateFullName = misc.toTitleCase(
    `${candidateFirstName} ${candidateLastName}`,
  );

  if (status === "pending") {
    // TODO: Loading state.
    return null;
  } else if (status === "error") {
    // TODO: Error state.
    return null;
  }

  return (
    <div className="flex gap-2">
      <div
        className={clsx(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
          candidacyInterest.isInterested ? "bg-green-700" : "bg-red-600",
        )}
      >
        {candidacyInterest.isInterested ? (
          <ThumbsUpIcon className="h-3 w-3 text-white" />
        ) : (
          <ThumbsDownIcon className="h-3 w-3 text-white" />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-slate-700">
          <UserName currentUserId={currentUserId} user={user} />{" "}
          {user.id === currentUserId ? "are" : "is"}{" "}
          {candidacyInterest.isInterested ? "interested" : "not interested"} in{" "}
          <span className="text-slate-900">{candidateFullName}</span>
        </p>
        <TimeAgo date={candidacyInterest.createdAt} />
      </div>
    </div>
  );
};

const CandidacyComment = ({
  candidacyComment,
  candidateFirstName,
  candidateLastName,
  currentUserId,
}: {
  candidacyComment: CandidacyCommentType;
  candidateFirstName: string;
  candidateLastName: string;
  currentUserId: number;
}) => {
  const { data: user, status } = useGetUser({
    userId: candidacyComment.userId,
  });

  const candidateFullName = misc.toTitleCase(
    `${candidateFirstName} ${candidateLastName}`,
  );

  if (status === "pending") {
    // TODO: Loading state.
    return null;
  } else if (status === "error") {
    // TODO: Error state.
    return null;
  }

  return (
    <div className="flex gap-2">
      <div className="relative">
        <Avatar initials={`${user.firstName[0]}${user.lastName[0]}`} />
        {user.role === Role.STAFFER && (
          <JobPipelineCandidateSheetVerificationBadge className="absolute right-[-6px] top-[15px]" />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-slate-700">
          <UserName currentUserId={currentUserId} user={user} /> left a comment
          on <span className="text-slate-900">{candidateFullName}</span>
        </p>
        <div className="border-l border-slate-400 pl-2">
          <p className="whitespace-pre-line text-sm text-slate-600">
            {candidacyComment.content}
          </p>
        </div>
        <TimeAgo date={candidacyComment.createdAt} />
      </div>
    </div>
  );
};

const CandidacyScoreChange = ({
  candidateFirstName,
  candidateLastName,
  currentUserId,
  candidacyScoreChange,
}: {
  candidateFirstName: string;
  candidateLastName: string;
  currentUserId: number;
  candidacyScoreChange: CandidacyScoreChangeType;
}) => {
  const { data: user, status } = useGetUser({
    userId: candidacyScoreChange.userId,
  });

  const candidateFullName = misc.toTitleCase(
    `${candidateFirstName} ${candidateLastName}`,
  );

  const renderCandidacyScoreChangeText = () => {
    if (!user) return null;

    // "Jane Smith updated John Doe's rating from 3/5 to 4/5"
    if (candidacyScoreChange.fromScore) {
      return (
        <>
          <UserName currentUserId={currentUserId} user={user} /> updated{" "}
          <span className="text-slate-900">{candidateFullName}&apos;s</span>{" "}
          rating from {candidacyScoreChange.fromScore}/5 to{" "}
          {candidacyScoreChange.toScore}/5
        </>
      );
    }

    // "Jane Smith assigned John Doe a 4/5 rating"
    return (
      <>
        <UserName currentUserId={currentUserId} user={user} /> assigned{" "}
        <span className="text-slate-900">{candidateFullName}</span> a{" "}
        {candidacyScoreChange.toScore}/5 rating
      </>
    );
  };

  if (status === "pending") {
    // TODO: Loading state.
    return null;
  } else if (status === "error") {
    // TODO: Error state.
    return null;
  }

  return (
    <div className="flex gap-2">
      <div className="relative">
        <Avatar initials={`${user.firstName[0]}${user.lastName[0]}`} />
        {user.role === Role.STAFFER && (
          <JobPipelineCandidateSheetVerificationBadge className="absolute right-[-6px] top-[15px]" />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-slate-700">
          {renderCandidacyScoreChangeText()}
        </p>
        <div className="flex items-center gap-2">
          {[...Array<number>(candidacyScoreChange.toScore)].map((_, i) => (
            <StarIcon
              key={i}
              className="h-4 w-4 fill-primary-900 text-primary-900"
            />
          ))}
          {[...Array<number>(5 - candidacyScoreChange.toScore)].map((_, i) => (
            <StarIcon
              key={i}
              className="h-4 w-4 fill-slate-300 text-slate-300"
            />
          ))}
        </div>
        <TimeAgo date={candidacyScoreChange.createdAt} />
      </div>
    </div>
  );
};

const CandidacyStatusChange = ({
  currentUserId,
  candidateFirstName,
  candidateLastName,
  candidacyStatusChange,
}: {
  currentUserId: number;
  candidateFirstName: string;
  candidateLastName: string;
  candidacyStatusChange: CandidacyStatusChangeType;
}) => {
  const { data: user, status } = useGetUser({
    userId: candidacyStatusChange.userId,
  });

  const candidateFullName = misc.toTitleCase(
    `${candidateFirstName} ${candidateLastName}`,
  );
  const isRejected =
    candidacyStatusChange.toStatus === CandidacyStatus.REJECTED;
  // TODO: Improve this...
  const shouldShowDirectTo =
    candidacyStatusChange.fromStatus !== CandidacyStatus.MATCHED &&
    candidacyStatusChange.fromStatus !== CandidacyStatus.INTERVIEW &&
    candidacyStatusChange.fromStatus !== CandidacyStatus.OFFER &&
    candidacyStatusChange.fromStatus !== CandidacyStatus.REJECTED;

  const renderAvatarOrIconContainer = () => {
    if (!user) return null;

    if (isRejected) {
      return <RejectedIconContainer />;
    } else if (shouldShowDirectTo) {
      return <MovedForwardIconContainer />;
    } else if (
      candidacyStatusChange.fromStatus &&
      candidacyStatusToRank[candidacyStatusChange.fromStatus] >
        candidacyStatusToRank[candidacyStatusChange.toStatus]
    ) {
      return <MovedBackwardIconContainer />;
    }

    return <MovedForwardIconContainer />;
  };

  const renderCandidacyStatusChangeText = () => {
    if (!user) return null;

    const toStatusLabel =
      candidacyStatusToLabel[candidacyStatusChange.toStatus];

    if (isRejected) {
      // "Jane Smith rejected John Doe"
      return (
        <>
          <UserName currentUserId={currentUserId} user={user} /> rejected{" "}
          <span className="text-slate-900">{candidateFullName}</span>
        </>
      );
    } else if (shouldShowDirectTo) {
      // "Jane Smith moved John Doe to Matched"
      return (
        <>
          <UserName currentUserId={currentUserId} user={user} /> moved{" "}
          <span className="text-slate-900">{candidateFullName}</span> to{" "}
          {toStatusLabel}
        </>
      );
    } else if (candidacyStatusChange.fromStatus) {
      // "Jane Smith moved John Doe from Applied to Shortlist"
      return (
        <>
          <UserName currentUserId={currentUserId} user={user} /> moved{" "}
          <span className="text-slate-900">{candidateFullName}</span> from{" "}
          {candidacyStatusToLabel[candidacyStatusChange.fromStatus]} to{" "}
          {toStatusLabel}
        </>
      );
    }

    // This case shouldn't be hit but we have it to be safe.
    // "Jane Smith moved John Doe to <notShortlist>"
    return (
      <>
        <UserName currentUserId={currentUserId} user={user} /> moved{" "}
        <span className="text-slate-900">{candidateFullName}</span> to{" "}
        {toStatusLabel}
      </>
    );
  };

  if (status === "pending") {
    // TODO: Loading state.
    return null;
  } else if (status === "error") {
    // TODO: Error state.
    return null;
  }

  return (
    <div className="flex gap-2">
      <div className="relative">
        {renderAvatarOrIconContainer()}
        {user.role === Role.STAFFER && (
          <JobPipelineCandidateSheetVerificationBadge className="absolute right-[-6px] top-[15px]" />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-slate-700">
          {renderCandidacyStatusChangeText()}
        </p>
        <TimeAgo date={candidacyStatusChange.createdAt} />
      </div>
    </div>
  );
};

const CandidacySummaryChange = ({
  currentUserId,
  candidateFirstName,
  candidateLastName,
  candidacySummaryChange,
}: {
  currentUserId: number;
  candidateFirstName: string;
  candidateLastName: string;
  candidacySummaryChange: CandidacySummaryChangeType;
}) => {
  const { data: user, status } = useGetUser({
    userId: candidacySummaryChange.userId,
  });

  const candidateFullName = misc.toTitleCase(
    `${candidateFirstName} ${candidateLastName}`,
  );

  if (status === "pending") {
    // TODO: Loading state.
    return null;
  } else if (status === "error") {
    // TODO: Error state.
    return null;
  }

  return (
    <div className="flex gap-2">
      <div className="relative">
        <Avatar initials={`${user.firstName[0]}${user.lastName[0]}`} />
        {user.role === Role.STAFFER && (
          <JobPipelineCandidateSheetVerificationBadge className="absolute right-[-6px] top-[15px]" />
        )}
      </div>
      <Collapsible className="group/collapsible">
        <div className="flex flex-col items-start gap-2">
          <p className="text-sm font-medium text-slate-700">
            <UserName currentUserId={currentUserId} user={user} /> updated the
            executive summary for{" "}
            <span className="text-slate-900">{candidateFullName}</span>
          </p>
          <CollapsibleTrigger asChild>
            <span className="flex cursor-pointer items-center gap-1">
              <p className="text-xs text-primary-900 group-data-[state=open]/collapsible:hidden">
                View more
              </p>
              <p className="text-xs text-primary-900 group-data-[state=closed]/collapsible:hidden">
                View less
              </p>
              <ChevronRight className="h-3 w-3 text-primary-900 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="border-l border-slate-400 pl-2">
              <p className="text-sm text-slate-600">
                <RichTextDisplay richText={candidacySummaryChange.toSummary} />
              </p>
            </div>
          </CollapsibleContent>
          <TimeAgo date={candidacySummaryChange.createdAt} />
        </div>
      </Collapsible>
    </div>
  );
};

const TimeAgo = ({ date }: { date: string }) => {
  return (
    <p className="text-sm text-slate-500">{dates.timeAgoForPipeline(date)}</p>
  );
};

const MovedBackwardIconContainer = () => {
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-500">
      <ArrowLeftIcon className="h-3 w-3 stroke-[3px] text-white" />
    </div>
  );
};

const MovedForwardIconContainer = () => {
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-900">
      <ArrowRightIcon className="h-3 w-3 stroke-[3px] text-white" />
    </div>
  );
};

const RejectedIconContainer = () => {
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-600">
      <XIcon className="h-3 w-3 stroke-[3px] text-white" />
    </div>
  );
};

const UserName = ({
  currentUserId,
  user,
}: {
  currentUserId: number;
  user: User;
}) => {
  return (
    <span className="text-slate-900">
      {user.id === currentUserId ? "You" : `${user.firstName} ${user.lastName}`}
    </span>
  );
};

export const JobPipelineCandidateSheetActivity = ({
  candidacyId,
  candidateFirstName,
  candidateLastName,
  className,
}: {
  candidacyId: string;
  candidateFirstName: string;
  candidateLastName: string;
  className?: string;
}) => {
  const {
    data: candidacySummaryChanges,
    status: candidacySummaryChangesStatus,
  } = useGetCandidacySummaryChanges({ candidacyId });

  const { data: candidacyStatusChanges, status: candidacyStatusChangesStatus } =
    useGetCandidacyStatusChanges({ candidacyId });

  const { data: candidacyScoreChanges, status: candidacyScoreChangesStatus } =
    useGetCandidacyScoreChanges({ candidacyId });

  const { data: candidacyComments, status: candidacyCommentsStatus } =
    useGetCandidacyComments({ candidacyId });

  const { data: candidacyInterests, status: candidacyInterestsStatus } =
    useGetCandidacyInterests({ candidacyId });

  if (
    candidacyStatusChangesStatus === "pending" ||
    candidacyScoreChangesStatus === "pending" ||
    candidacyCommentsStatus === "pending" ||
    candidacySummaryChangesStatus === "pending" ||
    candidacyInterestsStatus === "pending"
  ) {
    // TODO: Loading state.
    return null;
  } else if (
    candidacyStatusChangesStatus === "error" ||
    candidacyScoreChangesStatus === "error" ||
    candidacyCommentsStatus === "error" ||
    candidacySummaryChangesStatus === "error" ||
    candidacyInterestsStatus === "error"
  ) {
    // TODO: Error state.
    return null;
  } else {
    // Get current user from storage.
    const currentUser = storage.getUser();
    if (!currentUser?.id) {
      throw new Error("User not found in storage.");
    }

    // Thread candidacy comments, candidacy score changes, and candidacy status changes together.
    const activities: Activity[] = [
      ...candidacyComments.map((candidacyComment) => {
        return {
          type: "candidacyComment" as const,
          candidacyComment,
          createdAt: candidacyComment.createdAt,
        };
      }),
      ...candidacyScoreChanges.map((candidacyScoreChange) => {
        return {
          type: "candidacyScoreChange" as const,
          candidacyScoreChange,
          createdAt: candidacyScoreChange.createdAt,
        };
      }),
      ...candidacyStatusChanges.map((candidacyStatusChange) => {
        return {
          type: "candidacyStatusChange" as const,
          candidacyStatusChange,
          createdAt: candidacyStatusChange.createdAt,
        };
      }),
      ...candidacySummaryChanges.map((candidacySummaryChange) => {
        return {
          type: "candidacySummaryChange" as const,
          candidacySummaryChange,
          createdAt: candidacySummaryChange.createdAt,
        };
      }),
      ...candidacyInterests.map((candidacyInterest) => {
        return {
          type: "candidacyInterest" as const,
          candidacyInterest,
          createdAt: candidacyInterest.createdAt,
        };
      }),
    ];

    activities.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return (
      <div className={clsx("flex flex-col gap-4", className)}>
        {activities.map((activity) => {
          if (activity.type === "candidacyComment") {
            return (
              <CandidacyComment
                candidacyComment={activity.candidacyComment}
                candidateFirstName={candidateFirstName}
                candidateLastName={candidateLastName}
                currentUserId={currentUser.id}
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                key={`candidacyComment${activity.candidacyComment.id}`}
              />
            );
          } else if (activity.type === "candidacyScoreChange") {
            return (
              <CandidacyScoreChange
                candidacyScoreChange={activity.candidacyScoreChange}
                candidateFirstName={candidateFirstName}
                candidateLastName={candidateLastName}
                currentUserId={currentUser.id}
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                key={`candidacyScoreChange${activity.candidacyScoreChange.id}`}
              />
            );
          } else if (activity.type === "candidacyStatusChange") {
            return (
              <CandidacyStatusChange
                candidacyStatusChange={activity.candidacyStatusChange}
                candidateFirstName={candidateFirstName}
                candidateLastName={candidateLastName}
                currentUserId={currentUser.id}
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                key={`candidacyStatusChange${activity.candidacyStatusChange.id}`}
              />
            );
          } else if (activity.type === "candidacyInterest") {
            return (
              <CandidacyInterest
                candidacyInterest={activity.candidacyInterest}
                candidateFirstName={candidateFirstName}
                candidateLastName={candidateLastName}
                currentUserId={currentUser.id}
                key={`candidacyInterest${activity.candidacyInterest.id.toString()}`}
              />
            );
          } else {
            return (
              <CandidacySummaryChange
                candidacySummaryChange={activity.candidacySummaryChange}
                candidateFirstName={candidateFirstName}
                candidateLastName={candidateLastName}
                currentUserId={currentUser.id}
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                key={`candidacySummaryChange${activity.candidacySummaryChange.id}`}
              />
            );
          }
        })}
      </div>
    );
  }
};
